import { useNextStep } from 'nextstepjs';
import { matchPath } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

import Collapse from '@mui/material/Collapse';

import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';

import NavItem from './nav-item';
import { NavListProps, NavSubListProps, NavItemBaseProps } from '../types';

// ----------------------------------------------------------------------

const ROOT_PATH = '/dashboard';

export default function NavList({ data, depth, slotProps }: NavListProps) {
  const { currentStep } = useNextStep();

  const pathname = usePathname();

  const isRootPath = ROOT_PATH === pathname;

  const activeLink = useActiveLink(
    isRootPath ? data.path : data.path.replace(ROOT_PATH, ''),
    true,
    !isRootPath
  );
  const active =
    activeLink ||
    !!data.children?.some((child: NavItemBaseProps) =>
      matchPath({ path: child.path, end: false }, pathname)
    );

  const [openMenu, setOpenMenu] = useState(active);

  useEffect(() => {
    setOpenMenu(active);
  }, [active, pathname]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      setOpenMenu((prev) => !prev);
    }
  }, [data.children]);

  return (
    <>
      <NavItem
        open={openMenu}
        onClick={handleToggleMenu}
        //
        title={data.title}
        id={data.path.replace(/^\/|\/$/g, '').replace(/\//g, '-')}
        path={data.path}
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        //
        depth={depth}
        hasChild={!!data.children}
        externalLink={data.path.includes('http')}
        currentRole={slotProps?.currentRole}
        //
        active={active}
        className={active ? 'active' : ''}
        hasPermissionsPremium={data.hasPermissionsPremium}
        sx={{
          mb: `${slotProps?.gap}px`,
          ...(depth === 1 ? slotProps?.rootItem : slotProps?.subItem),
        }}
      />

      {!!data.children && (
        <Collapse in={openMenu || currentStep >= 10}>
          <NavSubList data={data.children} depth={depth} slotProps={slotProps} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function NavSubList({ data, depth, slotProps }: NavSubListProps) {
  return (
    <>
      {data.map((list) => (
        <NavList key={list.title} data={list} depth={depth + 1} slotProps={slotProps} />
      ))}
    </>
  );
}
